// src/services/paths/bridges.ts
//
// Bridges of the safe-routing boundary. There are two kinds:
//
//  - `manual`: the pull-in zones the mapmaker drew. They are mission data and
//    always visible in the map.
//  - `auto`: the rectangles `SmartPathBuilder` builds at planning time for a
//    mission point that ended up outside the prepared boundary (start, end, or
//    a core). Nothing in the app shows them, and they are built *after* the
//    unsafe zones are subtracted (utils.ts::computeGluedBoundaryRings unions the
//    bridges onto the zone-subtracted field on purpose), so an auto bridge
//    makes the ground it crosses walkable — including ground inside an UNSAFE
//    zone. That is how a mapmaking mission ended up with a path through a zone
//    whose drawn outline still contained the core (2026-10-13).
//
// This module owns the record the planner keeps, the warning it shows, and the
// session registry the Robot path overlay draws from.

import type { Feature, MultiPolygon, Polygon } from 'geojson';

export type SmartPathBridgeKind = 'manual' | 'auto';

export type SmartPathBridge = {
  kind: SmartPathBridgeKind;
  geometry: Feature<Polygon | MultiPolygon>['geometry'];
  /** `auto` only: id of the mission point the bridge was built for
   *  (`nav-starting-position`, `nav-end-position`, or a core id). */
  pointId?: string;
};

/** Orange for the bridges we built, purple for the mapmaker's pull-in zones. */
export const BRIDGE_AUTO_COLOR = '#FF8C00';
export const BRIDGE_MANUAL_COLOR = '#7B1FA2';
export const BRIDGE_AUTO_FILL = 'rgba(255, 140, 0, 0.18)';
export const BRIDGE_MANUAL_FILL = 'rgba(123, 31, 162, 0.10)';

/**
 * What to tell the mapmaker about the bridges in the boundary, or undefined
 * when there are none. Every bridge is listed: the auto ones by the point they
 * were built for, the manual ones by count. The text always carries the
 * consequence — a bridge stays walkable where it crosses an UNSAFE zone.
 */
export function bridgeWarning(bridges: SmartPathBridge[]): string | undefined {
  if (bridges.length === 0) {
    return undefined;
  }

  const auto = bridges.filter((bridge) => bridge.kind === 'auto');
  const manualCount = bridges.length - auto.length;
  const parts: string[] = [];

  if (auto.length > 0) {
    const points = auto.map((bridge) => bridge.pointId ?? 'unknown point').join(', ');
    parts.push(`${auto.length} bridge${auto.length === 1 ? '' : 's'} added for ${points}`);
  }

  if (manualCount > 0) {
    parts.push(`${manualCount} pull-in zone${manualCount === 1 ? '' : 's'}`);
  }

  return `Safe boundary bridges: ${parts.join(' + ')}. A bridge stays walkable where it crosses an UNSAFE zone — check the zone if the path runs through one.`;
}

/**
 * The one boundary notice a planning run shows, or undefined when there is
 * nothing to say. The app shows a single warning at a time, so the bridges and
 * the buffering issues have to travel together — a bridge warning that fires
 * first is otherwise overwritten by the buffering one that follows (found
 * 2026-10-13: the mapmaker saw the buffer warning and never learned that the
 * bridge through the UNSAFE zone had been built).
 */
export function boundaryNotices(bridges: SmartPathBridge[], bufferWarnings: string[]): string | undefined {
  const notices: string[] = [];
  const bridgeNotice = bridgeWarning(bridges);

  if (bridgeNotice) {
    notices.push(bridgeNotice);
  }

  if (bufferWarnings.length > 0) {
    notices.push(`Boundary buffering issues: ${bufferWarnings.join('; ')}`);
  }

  return notices.length > 0 ? notices.join(' ') : undefined;
}

// ---------------------------------------------------------------------------
// Session registry
// ---------------------------------------------------------------------------
//
// Keyed by mission instance id (Active-Record instances are singletons per
// instance_id). Not persisted: after a reload only the pull-in zones — which
// are mission data — can be drawn; an auto bridge is a property of the planning
// run that built it.

const bridgesByInstance = new Map<number, SmartPathBridge[]>();

export function rememberSmartPathBridges(instanceId: number, bridges: SmartPathBridge[]): void {
  bridgesByInstance.set(
    instanceId,
    bridges.map((bridge) => ({ ...bridge })),
  );
}

export function getSmartPathBridges(instanceId: number): SmartPathBridge[] {
  return bridgesByInstance.get(instanceId) ?? [];
}

export function forgetSmartPathBridges(instanceId: number): void {
  bridgesByInstance.delete(instanceId);
}

/**
 * What the Robot path overlay should draw for a mission: the bridges of the
 * last planning run when there was one, otherwise the pull-in zones — those are
 * mission data, so they are drawable before any "Find Path" in this session.
 * (An auto bridge cannot come back after a reload: it is a property of the
 * planning run that built it. Persisting it is a follow-up.)
 */
export function bridgesForOverlay(
  instanceId: number,
  pullinZones: { toMultiPolygon(): Feature<MultiPolygon> }[],
): SmartPathBridge[] {
  const remembered = getSmartPathBridges(instanceId);

  if (remembered.length > 0) {
    return remembered;
  }

  return pullinZones.map((zone) => ({ kind: 'manual' as const, geometry: zone.toMultiPolygon().geometry }));
}
